/**
 * Redis Credential Form
 *
 * A form for configuring Redis credentials with:
 * - Host, port, password configuration
 * - Database index selection
 * - SSL/TLS option
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export interface RedisFormData {
  host: string;
  port: string;
  password: string;
  database: string;
  ssl: boolean;
}

interface RedisCredentialFormProps {
  formData: RedisFormData;
  onChange: (data: Partial<RedisFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
}

export function RedisCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: RedisCredentialFormProps) {
  const handleChange = (field: keyof RedisFormData, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="host">
            Host <span className="text-red-500">*</span>
          </Label>
          <Input
            id="host"
            placeholder="localhost"
            value={formData.host}
            onChange={(e) => handleChange('host', e.target.value)}
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="port">Port</Label>
          <Input
            id="port"
            placeholder="6379"
            value={formData.port}
            onChange={(e) => handleChange('port', e.target.value)}
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder={isEditing ? 'Leave unchanged or enter new' : 'Enter password (if required)'}
          value={formData.password}
          onChange={(e) => handleChange('password', e.target.value)}
          autoComplete="new-password"
          className="font-mono text-sm"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="database">Database Index</Label>
        <Input
          id="database"
          placeholder="0"
          value={formData.database}
          onChange={(e) => handleChange('database', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="ssl">Use SSL/TLS</Label>
          <p className="text-xs text-muted-foreground">Enable encrypted connection</p>
        </div>
        <Switch id="ssl" checked={formData.ssl} onCheckedChange={(checked) => handleChange('ssl', checked)} />
      </div>
    </div>
  );
}
